import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/SEO';

function YouTubeEmbed({ videoId, title }: { videoId: string; title: string }) {
  return (
    <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-foreground-100">
      <iframe
        src={`https://www.youtube.com/embed/${videoId}`}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="absolute inset-0 w-full h-full"
        loading="lazy"
      />
    </div>
  );
}

export default function Videos() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isZh = i18n.language === 'zh';
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const title = isZh
    ? '影片專區｜國際扶輪和平意象｜澎湖和平意象紀念碑'
    : 'Videos | Rotary International Peace Monument | Penghu Peace Monument';
  const description = isZh
    ? '觀看國際扶輪和平意象紀念碑的完整影片，了解和平鐘、12根立柱、213種和平文字的設計理念與精神象徵。從影像中看見和平意象的誕生。'
    : 'Watch the full video about the Rotary International Peace Monument. Discover the Peace Bell, 12 pillars, 213 languages of peace, and the design philosophy behind this landmark.';
  const keywords = isZh
    ? '影片專區, 國際扶輪, 和平意象, 和平鐘, 公共藝術影片, 澎湖紀念碑'
    : 'Videos, Rotary International, Peace Monument, Peace Bell, Public Art Video, Penghu Landmark';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    url: `${siteUrl}/videos`,
    description,
    inLanguage: isZh ? 'zh-TW' : 'en',
    isPartOf: {
      '@type': 'WebSite',
      url: `${siteUrl}/`,
    },
    mainEntity: {
      '@type': 'VideoObject',
      name: t('videos.feature.title'),
      description: t('videos.feature.desc'),
      thumbnailUrl: 'https://img.youtube.com/vi/pl32TNKFDww/maxresdefault.jpg',
      uploadDate: '2026-05-26',
      embedUrl: 'https://www.youtube.com/embed/pl32TNKFDww',
    },
  };

  const chapters = [
    { icon: 'ri-heart-line', titleKey: 'videos.card1.title', descKey: 'videos.card1.desc', color: 'bg-accent-50 text-accent-600' },
    { icon: 'ri-building-line', titleKey: 'videos.card2.title', descKey: 'videos.card2.desc', color: 'bg-primary-50 text-primary-600' },
    { icon: 'ri-translate-2', titleKey: 'videos.card3.title', descKey: 'videos.card3.desc', color: 'bg-secondary-50 text-secondary-600' },
    { icon: 'ri-time-line', titleKey: 'videos.card4.title', descKey: 'videos.card4.desc', color: 'bg-accent-50 text-accent-600' },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <SEO
        title={title}
        description={description}
        keywords={keywords}
        canonical="/videos"
        schema={schema}
        lang={isZh ? 'zh-TW' : 'en'}
      />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-24 md:pt-32 pb-12 md:pb-16 bg-background-50">
          <div className="max-w-5xl mx-auto px-4 md:px-8 text-center">
            <span className="inline-block text-xs md:text-sm text-accent-500 font-medium tracking-widest uppercase mb-4">
              {t('videos.label')}
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#0A2D5E] leading-tight mb-4">
              {t('videos.title')}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto mb-8">
              {t('videos.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => {
                  const el = document.getElementById('main-video');
                  el?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="group flex items-center gap-2 px-6 py-3 bg-[#0A2D5E] hover:bg-[#0C3A75] text-background-50 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap"
              >
                {t('videos.cta.play')}
                <i className="ri-play-circle-line text-sm" />
              </button>
              <button
                onClick={() => navigate('/media')}
                className="group flex items-center gap-2 px-6 py-3 border border-[#0A2D5E] text-[#0A2D5E] hover:bg-[#0A2D5E] hover:text-background-50 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap"
              >
                {t('videos.cta.media')}
                <i className="ri-arrow-right-line text-sm group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </section>

        {/* Featured Video */}
        <section id="main-video" className="py-12 md:py-20 bg-background-100">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
              {/* Left - Video */}
              <div className="w-full lg:w-3/5">
                <div className="relative rounded-2xl overflow-hidden shadow-xl">
                  <YouTubeEmbed videoId="pl32TNKFDww" title={t('videos.feature.title')} />
                </div>
              </div>

              {/* Right - Description */}
              <div className="w-full lg:w-2/5 flex flex-col justify-center">
                <span className="text-xs md:text-sm text-accent-500 font-medium tracking-widest uppercase mb-4">
                  {t('videos.feature.label')}
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#0A2D5E] leading-tight mb-2">
                  {t('videos.feature.title')}
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
                  {t('videos.feature.desc')}
                </p>
                <button
                  onClick={() => {
                    const el = document.getElementById('main-video');
                    const iframe = el?.querySelector('iframe');
                    if (iframe) {
                      iframe.src = iframe.src + '&autoplay=1';
                    }
                  }}
                  className="group flex items-center gap-2 px-6 py-3 bg-[#0A2D5E] hover:bg-[#0C3A75] text-background-50 rounded-full text-sm font-medium transition-all duration-300 w-fit whitespace-nowrap"
                >
                  <i className="ri-play-fill text-sm" />
                  {t('videos.feature.play')}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Chapter Cards */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-primary-700">
                {t('videos.chapters.title')}
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {chapters.map((chapter, index) => (
                <div
                  key={chapter.titleKey}
                  className="group bg-background-100 rounded-xl p-6 md:p-8 text-center hover:bg-background-50 transition-all duration-500 border border-background-200/50 hover:border-accent-200/50 hover:shadow-lg"
                >
                  <div className={`w-14 h-14 flex items-center justify-center mx-auto mb-5 rounded-full ${chapter.color} group-hover:scale-110 transition-transform duration-500`}>
                    <i className={`${chapter.icon} text-2xl`} />
                  </div>
                  {index === 0 && (
                    <span className="inline-block text-[10px] bg-[#0A2D5E] text-background-50 px-2 py-0.5 rounded-full mb-3">
                      Core
                    </span>
                  )}
                  <h3 className="font-heading text-base md:text-lg font-bold text-[#0A2D5E] mb-2">
                    {t(chapter.titleKey)}
                  </h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    {t(chapter.descKey)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}